/**
 * I took inspiration in few stack exchange posts
 * and Tencent vConsole library (MIT)
 * by wrapping the XMLHttpRequest object in a Proxy
 * we can intercept the network requests
 * in not-so-hacky way
 * */
import NetworkMessage from "./networkMessage";
import { formatByteSize, genStringBody, getStringResponseByType, getURL, } from "./utils";
export class ResponseProxyHandler {
    constructor(resp, item) {
        this.resp = resp;
        this.item = item;
    }
    set(target, key, value) {
        return Reflect.set(target, key, value);
    }
    get(target, key) {
        const value = Reflect.get(target, key);
        if (key === "arrayBuffer" || key === "blob") {
            return typeof value === "function" ? value.bind(target) : value;
        }
        switch (key) {
            case "formData":
            case "json":
            case "text":
                return () => {
                    this.item.responseType = key.toLowerCase();
                    // @ts-ignore
                    return value.apply(target).then((resp) => {
                        this.item.response = getStringResponseByType(this.item.responseType, resp);
                        return resp;
                    });
                };
        }
        if (typeof value === "function") {
            return value.bind(target);
        }
        else {
            return value;
        }
    }
}
export class FetchProxyHandler {
    constructor(ignoredHeaders, setSessionTokenHeader, sanitize, sendMessage, isServiceUrl, tokenUrlMatcher) {
        this.ignoredHeaders = ignoredHeaders;
        this.setSessionTokenHeader = setSessionTokenHeader;
        this.sanitize = sanitize;
        this.sendMessage = sendMessage;
        this.isServiceUrl = isServiceUrl;
        this.tokenUrlMatcher = tokenUrlMatcher;
    }
    apply(target, _, argsList) {
        var _a;
        const input = argsList[0];
        const init = argsList[1];
        if (!input ||
            // @ts-ignore
            (typeof input !== "string" && !(input === null || input === void 0 ? void 0 : input.url))) {
            return target.apply(window, argsList);
        }
        const isORUrl = input instanceof URL || typeof input === "string"
            ? this.isServiceUrl(String(input))
            : this.isServiceUrl(String(input.url));
        if (isORUrl) {
            return target.apply(window, argsList);
        }
        const item = new NetworkMessage(this.ignoredHeaders, this.setSessionTokenHeader, this.sanitize);
        this.beforeFetch(item, input, init);
        const signal = (argsList[0] instanceof Request ? argsList[0].signal : undefined) ||
            ((_a = argsList[1]) === null || _a === void 0 ? void 0 : _a.signal);
        // guard to avoid double-send
        let abortedNotified = false;
        const notifyAbort = () => {
            if (abortedNotified)
                return;
            abortedNotified = true;
            item.endTime = performance.now();
            item.duration = item.endTime - (item.startTime || item.endTime);
            item.status = 0;
            item.statusText = "Aborted";
            item.readyState = 0;
            const msg = item.getMessage();
            if (msg)
                this.sendMessage(msg);
        };
        if (signal) {
            if (signal.aborted) {
                notifyAbort();
            }
            else {
                signal.addEventListener("abort", notifyAbort, { once: true });
            }
        }
        this.setSessionTokenHeader((name, value) => {
            if (this.tokenUrlMatcher !== undefined) {
                if (!this.tokenUrlMatcher(item.url)) {
                    return;
                }
            }
            if (argsList[1] === undefined && argsList[0] instanceof Request) {
                return argsList[0].headers.append(name, value);
            }
            else {
                if (!argsList[1])
                    argsList[1] = {};
                if (argsList[1].headers === undefined) {
                    argsList[1] = Object.assign(Object.assign({}, argsList[1]), { headers: {} });
                }
                if (argsList[1].headers instanceof Headers) {
                    argsList[1].headers.append(name, value);
                }
                else if (Array.isArray(argsList[1].headers)) {
                    argsList[1].headers.push([name, value]);
                }
                else {
                    // @ts-ignore
                    argsList[1].headers[name] = value;
                }
            }
        });
        return target.apply(window, argsList)
            .then(this.afterFetch(item, () => {
            abortedNotified = true;
        }))
            .catch((e) => {
            item.endTime = performance.now();
            item.duration = item.endTime - (item.startTime || item.endTime);
            if (e && e.name === "AbortError") {
                item.status = 0;
                item.statusText = "Aborted";
                item.readyState = 0;
                if (!abortedNotified) {
                    const msg = item.getMessage();
                    if (msg)
                        this.sendMessage(msg);
                }
            }
            throw e;
        });
    }
    beforeFetch(item, input, init) {
        let url, method = "GET", requestHeader = {};
        // handle `input` content
        if (typeof input === "string") {
            // when `input` is a string
            method = (init === null || init === void 0 ? void 0 : init.method) || "GET";
            url = getURL(input);
            requestHeader = (init === null || init === void 0 ? void 0 : init.headers) || {};
        }
        else {
            // when `input` is a `Request` object
            method = input.method || "GET";
            url = getURL(input.url);
            requestHeader = input.headers;
        }
        item.method = method;
        item.requestType = "fetch";
        item.requestHeader = requestHeader;
        item.url = url.toString();
        item.name = (url.pathname.split("/").pop() || "") + url.search;
        item.status = 0;
        item.statusText = "Pending";
        item.readyState = 1;
        if (!item.startTime) {
            // UNSENT
            item.startTime = performance.now();
        }
        if (Object.prototype.toString.call(requestHeader) === "[object Headers]") {
            item.requestHeader = {};
            for (const [key, value] of requestHeader) {
                item.requestHeader[key] = value;
            }
        }
        else {
            item.requestHeader = requestHeader;
        }
        // save GET data
        if (url.search && url.searchParams) {
            item.getData = {};
            for (const [key, value] of url.searchParams) {
                item.getData[key] = value;
            }
        }
        // save POST data
        if (init === null || init === void 0 ? void 0 : init.body) {
            item.requestData = genStringBody(init.body);
        }
    }
    afterFetch(item, onResolved) {
        return (resp) => {
            if (onResolved)
                onResolved === null || onResolved === void 0 ? void 0 : onResolved();
            item.endTime = performance.now();
            item.duration = item.endTime - (item.startTime || item.endTime);
            item.status = resp.status;
            item.statusText = String(resp.status);
            let isChunked = false;
            item.header = {};
            for (const [key, value] of resp.headers) {
                item.header[key] = value;
                isChunked =
                    value.toLowerCase().indexOf("chunked") > -1 ? true : isChunked;
            }
            if (isChunked) {
                // when `transfer-encoding` is chunked, the response is a stream which is under loading,
                // so the `readyState` should be 3 (Loading),
                // and the response should NOT be `clone()` which will affect stream reading.
                item.readyState = 3;
            }
            else {
                // Otherwise, not chunked, the response is not a stream,
                // so it's completed and can be cloned for `text()` calling.
                item.readyState = 4;
                this.handleResponseBody(resp.clone(), item)
                    .then((responseValue) => {
                    item.responseSize =
                        typeof responseValue === "string"
                            ? responseValue.length
                            : responseValue.byteLength;
                    item.responseSizeText = formatByteSize(item.responseSize);
                    item.response = getStringResponseByType(item.responseType, responseValue);
                    const msg = item.getMessage();
                    if (msg) {
                        this.sendMessage(msg);
                    }
                })
                    .catch((e) => {
                    if (e.name === "AbortError") {
                        item.status = 0;
                        item.statusText = "Aborted";
                        item.readyState = 0;
                        const msg = item.getMessage();
                        if (msg)
                            this.sendMessage(msg);
                    }
                    else {
                        throw e;
                    }
                });
            }
            const ct = (resp.headers.get("content-type") || "").toLowerCase();
            const isTextLike = ct.includes("application/json") || ct.startsWith("text/");
            return isTextLike
                ? new Proxy(resp, new ResponseProxyHandler(resp, item))
                : resp;
        };
    }
    handleResponseBody(resp, item) {
        // parse response body by Content-Type
        const contentType = resp.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
            item.responseType = "json";
            return resp.text();
        }
        else if (contentType &&
            (contentType.includes("text/html") || contentType.includes("text/plain"))) {
            item.responseType = "text";
            return resp.text();
        }
        else {
            item.responseType = "arraybuffer";
            return resp.arrayBuffer();
        }
    }
}
export default class FetchProxy {
    static create(ignoredHeaders, setSessionTokenHeader, sanitize, sendMessage, isServiceUrl, tokenUrlMatcher) {
        return new Proxy(fetch, new FetchProxyHandler(ignoredHeaders, setSessionTokenHeader, sanitize, sendMessage, isServiceUrl, tokenUrlMatcher));
    }
}
//# sourceMappingURL=fetchProxy.js.map